import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { signupUser } from "../../services/handlelogin";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AxiosError } from "axios";
import { useTheme } from "@/components/theme-provider";

const registrationSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(4, "Password must be 4 characters or more"),
  companyName: z.string().min(3).optional().or(z.literal("")),
});

const SignUp = () => {
  const [userType, setUserType] = useState("customer");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleUserTypeChange = (value: string) => {
    setUserType(value);
  };

  const signUpMutation = useMutation({
    mutationFn: async (data: {
      email?: string;
      password?: string;
      companyName?: string;
    }) => signupUser(data.email, data.password, data.companyName),
    onSuccess: () => {
      toast.success("Sign up successful!");
      navigate("/sign-in");
    },
    onError: (error) => {
      const newError = error as AxiosError<{ message: string }>;
      console.error(
        "An error occurred while adding the employee:",
        newError.response?.data?.message
      );
      toast.error(newError.response?.data?.message ?? "An error occurred");
    },
  });

  // Create form instance
  const registerForm = useForm<z.infer<typeof registrationSchema>>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      email: "",
      password: "",
      companyName: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof registrationSchema>) => {
    if (userType === "customer") {
      delete values["companyName"];
    } else if (values["companyName"].length < 3) {
      return toast.error("Please enter valid company name");
    }
    signUpMutation.mutate(values);
  };

  return (
    <div className="flex h-screen">
      <div className="md:w-1/2  hidden md:flex items-center justify-center p-8">
        <img
          src={theme === "dark" ? "/darkmode.jpg" : "/lightmode.jpg"}
          alt="Login"
          className="w-full h-3/4 object-cover rounded-lg shadow-lg"
        />
      </div>
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h2 className="text-4xl font-bold mb-6">Create an account</h2>
          <p className="mb-6">Sign up as a Customer or Company Admin</p>

          <Form {...registerForm}>
            <form onSubmit={registerForm.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-6 p-6 border rounded-md">
                <RadioGroup
                  name="userType"
                  value={userType}
                  onValueChange={handleUserTypeChange}
                  className="flex items-center space-x-4 mb-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="customer" id="customer" />
                    <Label htmlFor="customer">Customer</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="companyAdmin" id="companyAdmin" />
                    <Label htmlFor="companyAdmin">Company Admin</Label>
                  </div>
                </RadioGroup>
                <FormField
                  control={registerForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={registerForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="absolute right-2 top-2"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div
                  style={{
                    display: userType !== "companyAdmin" ? "none" : "block",
                  }}
                >
                  <FormField
                    control={registerForm.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Company Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full py-2 px-4 mt-2 rounded-lg"
                >
                  {signUpMutation.isPending ? "Signing up..." : "Sign Up"}
                </Button>
              </div>
            </form>
          </Form>

          <div className="text-center mt-4">
            <p className="text-sm">
              Already have an account?{" "}
              <Link to="/sign-in" className="text-primary">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
